import { apiClient } from "../api/apiClient";
import { isIpv4Address, isIpv6Address } from "../utils/ipAddress";
import type { PublicIpRiskResultDto } from "../types/dto";
import { buildIpRiskToolResult, buildToolErrorResult, buildValidationErrorResult, type WebMcpToolExecuteResult } from "./ipRiskToolResult";
import type { WebMcpModelContext, WebMcpRegisterToolOptions, WebMcpTool, WebMcpToolInput } from "./types";

// A single IP literal is at most 45 characters (an IPv4-mapped IPv6 address, e.g.
// "::ffff:255.255.255.255") — anything longer cannot be one, so it's rejected before even trying to
// parse it.
const MAX_IP_INPUT_LENGTH = 45;

// spec §二十一: single literal IP only — trimmed, length-capped, and checked against the SAME
// IPv4/IPv6 predicates the rest of the site already uses (web/src/utils/ipAddress.ts), so this never
// grows into a second, subtly different address parser. Rejects CIDR ("/24"), URLs, hostnames, multiple
// addresses and arbitrary text as a side effect of neither predicate accepting them — see
// isIpv6Address's doc comment for why that holds even for adversarial input.
function parseIpAddressInput(input: WebMcpToolInput): string {
  const raw = input?.ipAddress;
  if (typeof raw !== "string") {
    throw new Error("ipAddress must be a string.");
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new Error("ipAddress must not be empty.");
  }
  if (trimmed.length > MAX_IP_INPUT_LENGTH) {
    throw new Error("ipAddress is too long to be a single IP address.");
  }
  if (!isIpv4Address(trimmed) && !isIpv6Address(trimmed)) {
    throw new Error(
      "ipAddress must be a single literal IPv4 or IPv6 address (no CIDR, URL, hostname, port, or multiple addresses).",
    );
  }
  return trimmed;
}

async function queryIpRisk(body: { ipAddress?: string }): Promise<WebMcpToolExecuteResult> {
  try {
    const dto = await apiClient.post<PublicIpRiskResultDto>("/api/public/ip/query", body);
    return { success: true, result: buildIpRiskToolResult(dto) };
  } catch (error) {
    return buildToolErrorResult(error);
  }
}

async function executeCheckIpRisk(input: WebMcpToolInput): Promise<WebMcpToolExecuteResult> {
  let ipAddress: string;
  try {
    ipAddress = parseIpAddressInput(input);
  } catch (error) {
    return buildValidationErrorResult(error instanceof Error ? error.message : "Invalid ipAddress.");
  }
  return queryIpRisk({ ipAddress });
}

// The visitor's own address, resolved server-side exactly as HomePage's own auto-detect query does
// (same endpoint, empty body) — never a third-party IP-echo service (spec §五).
function executeCheckCurrentIp(): Promise<WebMcpToolExecuteResult> {
  return queryIpRisk({});
}

const CHECK_IP_RISK_TOOL: WebMcpTool = {
  name: "check_ip_risk",
  title: "Check IP Risk",
  description:
    "Analyze one IPv4 or IPv6 address with NetRiskScan. Returns the NetRiskScan Index, network and " +
    "connection classification, proxy/VPN/Tor signals, threat indicators and supporting network " +
    "metadata. The NetRiskScan Index ranges from 0 to 100; higher values mean a cleaner, lower-risk " +
    "IP. Use this tool when the user wants to evaluate the reputation, anonymity, proxy/VPN/Tor " +
    "status, or network type of a specific IP address.",
  inputSchema: {
    type: "object",
    properties: {
      ipAddress: {
        type: "string",
        description: "A public IPv4 or IPv6 address to analyze with NetRiskScan.",
      },
    },
    required: ["ipAddress"],
    additionalProperties: false,
  },
  // Read-only: no NetRiskScan state changes. Untrusted: hostname/ISP/organization/location text in the
  // result originates from upstream network intelligence, not from this page (spec §十一/§二十).
  annotations: { readOnlyHint: true, untrustedContentHint: true },
  execute: (input) => executeCheckIpRisk(input),
};

const CHECK_CURRENT_IP_TOOL: WebMcpTool = {
  name: "check_current_ip",
  title: "Check My Current IP",
  description:
    "Analyze the public IP address currently used by this browser with NetRiskScan. Returns the same " +
    "risk, network, anonymity and reputation assessment as a normal NetRiskScan lookup. Use this when " +
    "the user asks about their current IP, current connection, VPN/proxy/Tor status, or current " +
    "NetRiskScan Index.",
  inputSchema: {
    type: "object",
    properties: {},
    additionalProperties: false,
  },
  annotations: { readOnlyHint: true, untrustedContentHint: true },
  execute: () => executeCheckCurrentIp(),
};

// Registers both tools against `document.modelContext` — a no-op given `undefined` or a context
// without `registerTool` (a browser with no WebMCP support), so callers don't need their own feature
// detection. `registerTool` can reject, or (per a stricter host) throw synchronously; neither may
// become an unhandled rejection or break the page, since NetRiskScan has to keep working as an
// ordinary website regardless of whether this experimental API is implemented, enabled, or well-behaved.
export function registerWebMcpTools(context: WebMcpModelContext | undefined, options: WebMcpRegisterToolOptions): void {
  if (!context?.registerTool) {
    return;
  }

  for (const tool of [CHECK_IP_RISK_TOOL, CHECK_CURRENT_IP_TOOL]) {
    try {
      context
        .registerTool(tool, options)
        ?.catch((error: unknown) => {
          console.warn(`[webmcp] Failed to register tool "${tool.name}".`, error);
        });
    } catch (error) {
      console.warn(`[webmcp] Failed to register tool "${tool.name}".`, error);
    }
  }
}
